import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import MuiPhoneNumber from 'mui-phone-number';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { emptyCustomer } from '../../../data/customers';
import {
  RegionType,
  emptyRegion,
  getGovernorateForDistrict,
  regions,
} from '../../../data/regions';
import { CustomerType } from '../../../interfaces/common/customer';
import ConfirmAction from '../confirm-action/confirm-action';
import AddressInputWithAutocomplete from '../adress-input-with-autocomplete/address-input-with-autocomplete';
import { ErrorsFlattenner } from '../errors-flattenner/errors-flattenner';
import { MuiTelInput } from 'mui-tel-input';
import InputWithLegend from '../input-with-legend/input-with-legend';
import LocationsInput from '../locations-input/locations-input';
import { ILocation } from '../../../interfaces/common/location';
import { isMobile, isTablet } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import {
  MerchantType,
  CompactMerchantType,
} from '../../../interfaces/delivery-company/merchants';
import { UserType } from '../../../enums/users';
import AutocompleteWithThrottle from '../autocomplete-with-throttle/autocomplete-with-throttle';
import { MerchantWithIcon } from '../styles/merchant-with-icon';
import { useSnackBar } from '../../../contexts/snackbar';
import { useNavigate } from 'react-router-dom';
import { Info } from '@mui/icons-material';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../utils/permissions';
import ArabicKeyboardDialog from '../arabic-keyboard-dialog';
import TextFieldWithArabicKeyboard from '../text-field-with-arabic-keyboard';
import { CustomerWithIcon } from '../styles/customer-with-icon';
import { DisplayLocations } from '../locations-display';
export type CustomerEffect = (c: CustomerType) => void;
export type PhoneExistsCallback = (phone: string) => void;

interface CreateCustomerFormProps {
  defaultCustomerId?: string;
  successCallback?: CustomerEffect;
  forcedMerchant?: MerchantType | CompactMerchantType;
  allowReuseExisting?: boolean
}

function CreateCustomerForm({
  defaultCustomerId,
  successCallback,
  forcedMerchant,
  allowReuseExisting
}: CreateCustomerFormProps) {
  const [customerData, setCustomerData] = useState<CustomerType>();
  const [existingCustomer, setExistingCustomer] = useState<CustomerType | null>(null);
  const [controlledDefaultCustomerId, setControlledDefaultCustomerId] = useState<string | undefined>(defaultCustomerId)
  const [regionSearchInput, setRegionSearchInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [rawInputs, setRawInputs] = useState<string[]>([])
  const { showMessage } = useSnackBar();
  const navigate = useNavigate();
  const [confirmDeleteCustomer, setConfirmDeleteCustomer] =
    useState<boolean>(false);

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    const getAndSetCustomer = async () => {
      if (controlledDefaultCustomerId !== undefined && controlledDefaultCustomerId.length>0) {
        setIsLoading(true);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/customer/${controlledDefaultCustomerId}/`;

          const response = await axios.get<CustomerType>(endpoint, config);
          console.log(response)
          setResponse(response);
          setCustomerData(response.data);
        } catch (e) {
          setCustomerData(undefined);
          if (axios.isAxiosError(e)) {
            console.log(response)
            setMessage('Failed to load customer  data: ' + e.message);
          } else {
            setMessage('Failed to load customer  data');
          }
        }
        setIsLoading(false);
      } else {
        setCustomerData({
          ...emptyCustomer,
          merchant: forcedMerchant as MerchantType,
        });
      }
    };
    getAndSetCustomer();
  }, []);

  const _fetchExistingCustomer = async (): Promise<CustomerType | null> => {
    try {
      const response = await axios.get<{count: number; results: CustomerType[]}>(`${API_ENDPOINT}/v1/customers/?phone__equals=${encodeURIComponent(customerData!.phone_number)}&page_size=1`, config);
      return response.data.count == 1 ? response.data.results[0] : null
    } catch {
      return null;
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (customerData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving Customer...');
      const requestData = {
        name: customerData.name,
        phone_number: customerData.phone_number,
        email: customerData.email,
        locations: customerData.locations,
        merchant: customerData.merchant,
        secondary_phone_number: customerData.secondary_phone_number,
        notifications_disabled: customerData.notifications_disabled
      };

      try {
        let response;
        if (controlledDefaultCustomerId === undefined || controlledDefaultCustomerId.length == 0) {
          // create new
          const endpoint = `${API_ENDPOINT}/api/v1/customers/`;
          response = await axios.post<CustomerType>(
            endpoint,
            requestData,
            config
          );
          console.log(response)
          setCustomerData(response.data);
        } else {
          // edit existing
          const endpoint = `${API_ENDPOINT}/api/v1/customer/${customerData.id}/`;
          response = await axios.patch<CustomerType>(
            endpoint,
            requestData,
            config
          );
          setCustomerData(response.data);
        }
        setResponse(response);

        if (response.status >= 200 && response.status < 300) {
          setMessage('Customer saved successfully');
          successCallback && successCallback(response.data);
        }
      } catch (e: any) {
        console.log("Here we are: ", e)
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          console.log(e.response)
          // Check if this is a phone_exists error
          if (
            e.response?.data?.error_code === 'already_exists' &&
            allowReuseExisting
          ) {
            // Call the phone exists callback and stop loading
            setExistingCustomer(await _fetchExistingCustomer());
            setIsLoading(false);
            setLoadingMessage('');
            return;
          }
          setMessage('Failed to save customer: ' + e.response?.statusText);
        } else {
          setMessage('Failed to save customer');
        }
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const deleteCustomer = async () => {
    if (customerData) {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/customer/${customerData.id}/ `,
          config
        );
        setResponse(response);
        if (response.status === 204) {
          setCustomerData(undefined);
          setMessage('Customer deleted successfully');
          showMessage('Customer deleted successfully', 'success');
          navigate('/customers');
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setMessage('Failed to delete customer: ' + e.message);
        } else {
          setMessage('Failed to delete customer');
        }
      }
      setIsLoading(false);
    }
  };
  const handleFetchMerchants = async (
    query: string
  ): Promise<MerchantType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/merchants/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: MerchantType[];
      }>(endpoint, config);
      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  return (
    <Box>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {(controlledDefaultCustomerId === undefined || controlledDefaultCustomerId.length==0)  ? 'Create Customer' : 'Edit Customer'}
      </Typography>

      {isLoading ? (
        <>
          {loadingMessage} <br /> <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}

          {customerData && (
            <>
              <form onSubmit={handleSubmit}>
                {user?.type != UserType.MERCHANT && (
                  <>
                    {controlledDefaultCustomerId === undefined && !forcedMerchant ? (
                      <>
                        <AutocompleteWithThrottle
                          label={'Merchant'}
                          fetch={handleFetchMerchants}
                          value={
                            customerData.merchant != null
                              ? customerData.merchant
                              : null
                          }
                          getOptionLabel={(option: CompactMerchantType) =>
                            option.name + ' ' + option.phone_number
                          }
                          onChange={(e, value) => {
                            const merchant = value;
                            if (merchant != null)
                              setCustomerData({
                                ...customerData,
                                merchant: merchant,
                              });
                            else {
                              setCustomerData({
                                ...customerData,
                                merchant: null,
                              });
                            }
                          }}
                        />
                        <br />
                      </>
                    ) : (
                      <b>
                        <MerchantWithIcon
                          centered
                          merchant={customerData.merchant}
                        ></MerchantWithIcon>
                      </b>
                    )}
                  </>
                )}
                <FormControl fullWidth>
                  <TextFieldWithArabicKeyboard
                    autoComplete="off"
                    label="Customer Name"
                    type="text"
                    variant="outlined"
                    value={customerData.name}
                    onChange={(v) =>
                      setCustomerData({ ...customerData, name: v })
                    }
                  />
                </FormControl>

                <br />
                <br />

                <FormControl fullWidth>
                  <MuiTelInput
                    autoComplete="off"
                    forceCallingCode
                    label="Customer Phone Number"
                    defaultCountry="LB"
                    variant="outlined"
                    required
                    value={customerData.phone_number}
                    onChange={(newPhone: any) => {
                      if (typeof newPhone === 'string') {
                        setCustomerData({
                          ...customerData,
                          phone_number: newPhone,
                        });
                      } else
                        setCustomerData({
                          ...customerData,
                          phone_number: newPhone.target.value,
                        });
                    }}
                  />
                </FormControl>

                <br />
                <br />
                <FormControl fullWidth>
                  <MuiTelInput
                    autoComplete="off"
                    forceCallingCode
                    label="Secondary Phone Number"
                    defaultCountry="LB"
                    variant="outlined"
                    value={customerData.secondary_phone_number}
                    onChange={(newPhone: any) => {
                      if (typeof newPhone === 'string') {
                        setCustomerData({
                          ...customerData,
                          secondary_phone_number: newPhone,
                        });
                      } else
                        setCustomerData({
                          ...customerData,
                          secondary_phone_number: newPhone.target.value,
                        });
                    }}
                  />
                </FormControl>

                <br />
                <br />

                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    label="Customer Email"
                    type="email"
                    variant="outlined"
                    value={customerData.email}
                    onChange={(e) =>
                      setCustomerData({
                        ...customerData,
                        email: e.target.value,
                      })
                    }
                  />
                </FormControl>

                
                  <Box mt={2} mb={0}>
                   <Checkbox checked={customerData.notifications_disabled} onChange={() => setCustomerData({...customerData, notifications_disabled: !customerData.notifications_disabled})} /> 
                    <Tooltip sx={{display: "flex", alignItems: "center"}} title={"If checked, no automated notifications will be sent to the customer upon delivery."}><span>Disable notifications.  <Info fontSize='small' color='primary' sx={{cursor: 'pointer'}} /></span></Tooltip>
                  </Box>
              

                <LocationsInput
                  locations={customerData.locations}
                  setLocations={(locs) =>
                    setCustomerData({ ...customerData, locations: locs })
                  }
                  label={'Customer Locations'}
                />

                <br />
                <br />

                <div style={{ display: 'flex', gap: 7 }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    type="submit"
                    variant="contained"
                    disabled={user?.type != UserType.MERCHANT && controlledDefaultCustomerId != undefined && !user_has_data_model_access(user, DataResources.CUSTOMER, ResourceActionType.UPDATE)}
                  >
                    Save Customer
                  </Button>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    onClick={() => {
                      setConfirmDeleteCustomer(true);
                    }}
                    variant="outlined"
                    color="error"
                    disabled={(user?.type != UserType.MERCHANT && controlledDefaultCustomerId != undefined && !user_has_data_model_access(user, DataResources.CUSTOMER, ResourceActionType.DELETE)) || customerData.orders_count == undefined || customerData.orders_count > 0}
                  >
                    Delete Customer
                  </Button>
                  
                </div>
              </form>{' '}
            </>
          )}
        </>
      )}



      {existingCustomer && <ConfirmAction
        message={`This phone number ${customerData?.phone_number} is taken by:`}
        action={()=>{
          setCustomerData(existingCustomer!);
        setExistingCustomer(null);
          setControlledDefaultCustomerId(existingCustomer!.id)
        }}
        isOpen={existingCustomer != null}
        handleClose={() => setExistingCustomer(null)}
        cancel_label='Go Back'

      >
        <Box margin={"auto"} sx={{background: "#e0e0e0"}} p={2} borderRadius={5}>
          <CustomerWithIcon customer={existingCustomer} showPhone />
          <DisplayLocations locations={existingCustomer!.locations}/>
        </Box>

        <Alert sx={{mt: 2}} severity='info'>
          Do you wish to proceed with {existingCustomer!.name}?
        </Alert>
        
      </ConfirmAction>}

      <ConfirmAction
        message={'Are you sure you want to delete this Customer?'}
        action={deleteCustomer}
        isOpen={confirmDeleteCustomer}
        handleClose={() => setConfirmDeleteCustomer(false)}
        isSevereAction={true}
        confirmSevereActionTarget="delete customer"
      >
        <div>
          {' '}
          Attention: This action will delete all orders registered for this
          customer!
        </div>
      </ConfirmAction>
    </Box>
  );
}

export default CreateCustomerForm;
